// Conversation List Component
// Displays conversation history in sidebar

import { formatDistanceToNow } from 'date-fns';
import { MessageCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useChatStore } from '@/stores/chatStore';
import { createComponentLogger } from '@/utils/logger';
import type { AIConversation } from '@/types/chat';

const log = createComponentLogger('ConversationList', 'ai-chat');

// ============================================================================
// Component Props
// ============================================================================

interface ConversationListProps {
  conversations: AIConversation[];
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onNew: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export function ConversationList({
  conversations,
  activeConversationId,
  onSelect,
  onNew
}: ConversationListProps) {
  const { deleteConversation } = useChatStore();

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleDelete = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onSelect

    const confirmed = confirm('Delete this conversation? This cannot be undone.');
    if (!confirmed) {
return;
}

    log.info('Deleting conversation', { conversationId });
    await deleteConversation(conversationId);
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const formatLastMessageTime = (timestamp: string) => {
    try {
      const distance = formatDistanceToNow(new Date(timestamp), { addSuffix: false });
      // Shorten common phrases: "about 3 hours" -> "3h", "2 days" -> "2d"
      return distance
        .replace('about ', '')
        .replace(' seconds', 's')
        .replace(' second', 's')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' months', 'mo')
        .replace(' month', 'mo')
        .replace(' years', 'y')
        .replace(' year', 'y')
        .replace('less than a minute', 'now');
    } catch (error) {
      log.warn('Failed to format timestamp', { timestamp }, error as Error);
      return 'recently';
    }
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <MessageCircle className="h-12 w-12 text-muted-foreground mb-3" />
      <p className="text-sm text-muted-foreground mb-4">
        No conversations yet
      </p>
      <Button size="sm" onClick={onNew}>
        Start Chatting
      </Button>
    </div>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (conversations.length === 0) {
    return renderEmptyState();
  }

  return (
    <div className="divide-y divide-border/50">
      {conversations.map((conversation) => {
        const isActive = conversation.id === activeConversationId;

        return (
          <div
            key={conversation.id}
            onClick={() => onSelect(conversation.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(conversation.id);
              }
            }}
            className={`group w-full text-left p-3 hover:bg-accent/50 transition-all relative cursor-pointer ${
              isActive ? 'bg-accent border-l-2 border-primary' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {/* Title - clean, no icon */}
                <h4 className="text-sm font-normal truncate mb-1 text-foreground">
                  {conversation.title || 'New conversation'}
                </h4>

                {/* Compact metadata - time + message count only */}
                <div className="text-xs text-muted-foreground">
                  {formatLastMessageTime(conversation.last_message_at)} · {conversation.message_count} {conversation.message_count === 1 ? 'msg' : 'msgs'}
                </div>
              </div>

              {/* Delete Button - appears on hover */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity shrink-0"
                      onClick={(e) => handleDelete(conversation.id, e)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete conversation</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      })}
    </div>
  );
}
